import React, { useState, useEffect } from 'react';
import ElementForm from '../components/ElementForm';
import EnergyCharts from '../components/EnergyCharts';
import { getZodiacCrystals } from '../utils/dataManager';
import localforage from 'localforage';

const EnergyCalculator = () => {
  const [energyResult, setEnergyResult] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [zodiacCrystals, setZodiacCrystals] = useState([]);
  const [crystalTypes, setCrystalTypes] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  // 获取水晶类型数据
  useEffect(() => {
    const fetchCrystalTypes = async () => {
      try {
        const allCrystalTypes = await localforage.getItem('crystalTypes');
        if (allCrystalTypes) {
          setCrystalTypes(allCrystalTypes);
        }
      } catch (error) {
        console.error('获取水晶类型数据失败:', error);
      }
    };
    
    fetchCrystalTypes();
  }, []);

  // 当能量结果变化时，获取推荐水晶
  useEffect(() => {
    if (!energyResult || !userInfo) return;
    
    // 获取星座对应的水晶
    const zodiacRecommendations = getZodiacCrystals(userInfo.zodiacSign);
    setZodiacCrystals(zodiacRecommendations);
    
    // 获取推荐商品
    const fetchRecommendedProducts = async () => {
      try {
        const allProducts = await localforage.getItem('products');
        if (!allProducts) return;
        
        // 根据能量结果和星座推荐商品
        const recommendations = [];
        
        // 添加五行推荐的水晶对应的商品
        if (energyResult.recommendations && energyResult.recommendations.length > 0) {
          energyResult.recommendations.forEach(rec => {
            const crystalType = crystalTypes.find(type => type.name === rec.name);
            if (crystalType) {
              const matchingProducts = allProducts.filter(p => p.crystalTypeId === crystalType.id);
              if (matchingProducts.length > 0) {
                // 添加最受欢迎的产品
                recommendations.push(
                  ...matchingProducts
                    .sort((a, b) => b.popularity - a.popularity)
                    .slice(0, 1)
                );
              }
            }
          });
        }
        
        // 添加星座推荐的水晶对应的商品
        if (zodiacRecommendations && zodiacRecommendations.length > 0) {
          zodiacRecommendations.forEach(rec => {
            const crystalType = crystalTypes.find(type => type.name === rec.name);
            if (crystalType) {
              const matchingProducts = allProducts.filter(p => p.crystalTypeId === crystalType.id);
              if (matchingProducts.length > 0) {
                // 添加最受欢迎的产品
                recommendations.push(
                  ...matchingProducts
                    .sort((a, b) => b.popularity - a.popularity)
                    .slice(0, 1)
                );
              }
            }
          });
        }
        
        // 去重
        const uniqueRecommendations = recommendations.filter(
          (product, index, self) => 
            index === self.findIndex(p => p.id === product.id)
        );
        
        setRecommendedProducts(uniqueRecommendations.slice(0, 4));
      } catch (error) {
        console.error('获取推荐商品失败:', error);
      }
    };
    
    fetchRecommendedProducts();
  }, [energyResult, userInfo, crystalTypes]);

  // 处理计算结果
  const handleCalculateResult = (result, formData) => {
    console.log('计算结果:', result);
    setEnergyResult(result);
    setUserInfo(formData);
    
    // 滚动到结果区域
    setTimeout(() => {
      const resultsElement = document.getElementById('results');
      if (resultsElement) {
        try {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        } catch (error) {
          console.warn('滚动到结果区域失败:', error);
        }
      }
    }, 100);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold">五行能量计算器</h1>
        <p className="text-gray-600 mt-2">发现适合你的水晶，平衡五行能量</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* 表单区域 */}
        <div className="md:col-span-1">
          <ElementForm onCalculate={handleCalculateResult} />
        </div>
        
        {/* 结果区域 */}
        <div className="md:col-span-2" id="results">
          {energyResult ? (
            <div className="space-y-8">
              {/* 能量图表 */}
              <div className="bg-white bg-opacity-90 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-6 text-center">能量分析结果</h2>
                <EnergyCharts energyData={energyResult} />
              </div>
              
              {/* 水晶推荐 */}
              <div className="bg-white bg-opacity-90 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-center">水晶推荐</h2>
                
                {/* 五行推荐 */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">五行能量推荐</h3>
                  {energyResult.recommendations && energyResult.recommendations.length > 0 ? (
                    <ul className="space-y-2">
                      {energyResult.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <div>
                            <span className="font-medium">{rec.name}</span>
                            <p className="text-gray-600 text-sm">{rec.reason}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">暂无推荐</p>
                  )}
                </div>
                
                {/* 星座推荐 */}
                {userInfo && userInfo.zodiacSign && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-3">
                      {userInfo.zodiacSign}守护水晶
                    </h3>
                    {zodiacCrystals && zodiacCrystals.length > 0 ? (
                      <ul className="space-y-2">
                        {zodiacCrystals.map((crystal, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-primary mr-2">•</span>
                            <div>
                              <span className="font-medium">{crystal.name}</span>
                              <p className="text-gray-600 text-sm">{crystal.reason}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500">暂无推荐</p>
                    )}
                  </div>
                )}
                
                {/* 佩戴建议 */}
                <div>
                  <h3 className="text-lg font-medium mb-3">佩戴建议</h3>
                  <p className="text-gray-700">
                    根据您的五行能量分析，建议选择能够平衡您能量的水晶首饰。
                    您可以将水晶佩戴在对应脉轮位置，以获得最佳的能量平衡效果。
                    日常佩戴时，建议定期清洁水晶，并在月圆之夜为水晶充能。
                  </p>
                </div>
              </div>
              
              {/* 推荐商品 */}
              {recommendedProducts.length > 0 && (
                <div className="bg-white bg-opacity-90 rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-4 text-center">为您推荐的商品</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {recommendedProducts.map(product => (
                      <a
                        key={product.id}
                        href={`/product/${product.id}`}
                        className="flex items-center p-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-md mr-3"
                        />
                        <div>
                          <h4 className="font-medium text-gray-800">{product.name}</h4>
                          <p className="text-primary font-bold">¥{product.price}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white bg-opacity-90 rounded-lg shadow-md p-6 flex flex-col items-center justify-center h-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-gray-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <p className="text-gray-500 text-center">
                请填写左侧表单，获取您的五行能量分析和水晶推荐
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnergyCalculator;